import { OPS_COLORS } from '../constants/operationsDashboardColors';
import { CampaignSummaryItem, getCampaignHealthDisplay, getCampaignStaffingStatus } from './operationsDashboardUtils';

export const COMPACT_ROW_HEIGHT = 28;
export const COMPACT_HEADER_HEIGHT = 26;
export const COMPACT_CELL_PADDING = '2px 6px';

export interface CompactStatusBadge {
  label: string;
  bgcolor: string;
  color: string;
}

export function getCompactAttendanceBadge(row: {
  attendanceStatus: string;
  attendanceLabel?: string;
  coverageMode?: string | null;
  isCoveringOthers?: boolean;
}): CompactStatusBadge {
  if (row.attendanceLabel === 'Present (Late)') {
    return { label: 'Present (Late)', bgcolor: '#fff8e1', color: OPS_COLORS.late };
  }
  if (row.coverageMode === 'FULL_TRANSFER') {
    return { label: 'TRANSFER', bgcolor: '#ede7f6', color: '#5e35b1' };
  }
  if (row.isCoveringOthers) {
    return { label: 'COVERING', bgcolor: '#e3f2fd', color: OPS_COLORS.coverage };
  }
  switch (row.attendanceStatus) {
    case 'PRESENT':
      return { label: 'PRESENT', bgcolor: '#e8f5e9', color: OPS_COLORS.present };
    case 'SLIGHTLY_LATE':
      return { label: 'LATE', bgcolor: '#fff8e1', color: '#f57f17' };
    case 'LATE':
      return { label: 'LATE', bgcolor: '#fff8e1', color: OPS_COLORS.late };
    case 'NO_CALL_NO_SHOW':
      return { label: 'NO SHOW', bgcolor: '#ffebee', color: OPS_COLORS.critical };
    case 'ABSENT':
      return { label: 'NO SHOW', bgcolor: '#ffebee', color: OPS_COLORS.critical };
    case 'SCHEDULED_OFF':
      return { label: 'OFF', bgcolor: '#f5f5f5', color: OPS_COLORS.scheduledOff };
    case 'EXPECTED':
      return { label: 'EXPECTED', bgcolor: '#eceff1', color: OPS_COLORS.expected };
    default:
      return { label: (row.attendanceStatus || '—').replace(/_/g, ' '), bgcolor: '#f5f5f5', color: OPS_COLORS.expected };
  }
}

export function getCompactCoverageBadge(status: string): CompactStatusBadge {
  switch (status) {
    case 'TRANSFERRED':
      return { label: 'Transferred', bgcolor: '#ede7f6', color: '#5e35b1' };
    case 'COVERING':
      return { label: 'Covering', bgcolor: '#e3f2fd', color: OPS_COLORS.coverage };
    case 'COVERED':
      return { label: 'Covered', bgcolor: '#e3f2fd', color: OPS_COLORS.coverage };
    case 'PARTIALLY_COVERED':
      return { label: 'Partial', bgcolor: '#e8eaf6', color: '#3949ab' };
    case 'NOT_COVERED':
      return { label: 'No Coverage', bgcolor: '#fff3e0', color: OPS_COLORS.uncovered };
    case 'NOT_REQUIRED':
      return { label: 'Not Required', bgcolor: '#f5f5f5', color: OPS_COLORS.scheduledOff };
    default:
      return { label: status?.replace(/_/g, ' ') || '—', bgcolor: '#f5f5f5', color: OPS_COLORS.expected };
  }
}

/** Subtle row background by operational status. */
export function getCompactRowHighlight(row: {
  attendanceStatus: string;
  coverageStatus: string;
  requiresCoverage?: boolean;
  isCoveringOthers?: boolean;
}): string | undefined {
  if (row.coverageStatus === 'NOT_COVERED' && row.requiresCoverage) return '#fff3e0';
  if (row.isCoveringOthers) return '#e3f2fd';
  if (row.attendanceStatus === 'PRESENT') return '#f1f8f4';
  if (row.attendanceStatus === 'SLIGHTLY_LATE' || row.attendanceStatus === 'LATE') return '#fffde7';
  if (['ABSENT', 'NO_CALL_NO_SHOW'].includes(row.attendanceStatus)) return '#ffebee';
  if (row.attendanceStatus === 'SCHEDULED_OFF') return '#fafafa';
  return undefined;
}

export function getCampaignStaffingFraction(campaign: CampaignSummaryItem): {
  working: number;
  required: number;
  percent: number;
  shortageLabel: string;
} {
  const staffing = getCampaignStaffingStatus(campaign);
  const working = campaign.present + campaign.late;
  const required = campaign.scheduled;
  const percent = required > 0 ? Math.min(100, Math.round((working / required) * 100)) : 0;
  const shortage = Math.max(0, Math.ceil(staffing.openPositions));
  let shortageLabel = 'Healthy';
  if (shortage > 0) shortageLabel = `Needs ${shortage}`;
  else if (required > 0 && working < required) shortageLabel = `Needs ${required - working}`;
  return { working, required, percent, shortageLabel };
}

export function getCampaignHealthBadge(campaign: CampaignSummaryItem): CompactStatusBadge {
  const health = getCampaignHealthDisplay(campaign);
  const colors: Record<string, { bgcolor: string; color: string }> = {
    healthy: { bgcolor: '#e8f5e9', color: OPS_COLORS.present },
    monitor: { bgcolor: '#fff8e1', color: OPS_COLORS.warning },
    warning: { bgcolor: '#fff3e0', color: OPS_COLORS.risk },
    critical: { bgcolor: '#ffebee', color: OPS_COLORS.critical },
    not_scheduled: { bgcolor: '#f5f5f5', color: OPS_COLORS.scheduledOff },
  };
  const palette = colors[health.level] || colors.not_scheduled;
  return { label: health.label, bgcolor: palette.bgcolor, color: palette.color };
}

export function buildStaffingBarSegments(filled: number, total: number, width = 8): string {
  if (total <= 0) return '░'.repeat(width);
  const filledBlocks = Math.round((filled / total) * width);
  return `${'█'.repeat(Math.min(width, filledBlocks))}${'░'.repeat(Math.max(0, width - filledBlocks))}`;
}
